<template>
  <div v-if="focusKnowledge" class="d-flex align-items-center">
    <base-dropdown class="cursor p-0" position="right">
      <!-- DROPDOWN ELLIPSIS -->
      <a
        slot="title"
        class="cursor"
        role="button"
        data-toggle="dropdown my-auto"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <div class="nav-link-icon d-flex align-items-center">
          <div>
            <font-awesome-icon
              class="collection-dropdown-trigger-icon"
              :icon="['fal', 'ellipsis-h']"
            />
          </div>
        </div>
      </a>
      <!-- IMPORT OPTION -->
      <not-allowed-tooltip
        v-if="options.integrationImport"
        :user-permissions="userPermissions"
        permission-key="canUpdateContent"
      >
        <template v-slot:main-content="{ hasPermissions }">
          <div
            class="d-flex dropdown-item align-items-center"
            @click="hasPermissions ? $emit('open-integration-modal') : ''"
          >
            <div class="item-icon-wrapper">
              <font-awesome-icon
                class="mr-2 item-icon text-grey"
                :icon="['fad', 'download']"
              />
            </div>
            <span>{{ $t('knowledge.actions.import-article') }} </span>
          </div>
        </template>
      </not-allowed-tooltip>

      <!-- CONTENT SETTINGS -->
      <div
        v-if="options.contentSettings"
        class="d-flex dropdown-item align-items-center cursor"
        @click="$emit('open-content-settings', !options.isSettingsDrawerOpen)"
      >
        <div class="item-icon-wrapper">
          <font-awesome-icon
            class="mr-2 item-icon text-grey"
            :icon="['fas', 'cog']"
          />
        </div>
        <span class="mb-0">
          {{
            options.isSettingsDrawerOpen
              ? $t('knowledge.actions.content-settings.close')
              : $t('knowledge.actions.content-settings.open')
          }}
        </span>
      </div>

      <!-- COPY TO CLIPBOARD -->
      <div
        v-if="options.copyToClipboard"
        class="d-flex dropdown-item align-items-center cursor"
        @click="copyUrl()"
      >
        <div class="item-icon-wrapper">
          <font-awesome-icon
            class="mr-2 item-icon text-grey"
            :icon="['fad', 'clipboard']"
          />
        </div>
        <span class="mb-0">{{
          $t('knowledge.actions.copy-to-clipboard')
        }}</span>
      </div>

      <!-- CLONE OPTION -->
      <not-allowed-tooltip
        v-if="options.cloneCase"
        :user-permissions="userPermissions"
        permission-key="canCreateContent"
      >
        <template v-slot:main-content="{ hasPermissions }">
          <div
            class="d-flex dropdown-item align-items-center"
            @click="hasPermissions ? $emit('open-clone-modal') : ''"
          >
            <div class="item-icon-wrapper">
              <font-awesome-icon
                class="mr-2 item-icon text-grey"
                :icon="['fad', 'clone']"
              />
            </div>
            <span class="mb-0"
              >{{ $t('knowledge.actions.clone-in-other-kb') }}
            </span>
          </div>
        </template>
      </not-allowed-tooltip>

      <!-- ARCHIVE CASE OPTION -->
      <not-allowed-tooltip
        v-if="options.deleteCase"
        :user-permissions="userPermissions"
        permission-key="canDeleteContent"
      >
        <template v-slot:main-content="{ hasPermissions }">
          <div
            class="d-flex dropdown-item align-items-center"
            @click="hasPermissions ? $emit('open-delete-modal') : ''"
          >
            <div class="item-icon-wrapper">
              <font-awesome-icon
                class="mr-2 item-icon text-red"
                :icon="['fad', 'trash']"
              />
            </div>
            <span class="mb-0">{{ $t('knowledge.actions.delete') }}</span>
          </div>
        </template>
      </not-allowed-tooltip>

      <!-- CONTENT VERSION OPTION -->
      <div
        v-if="options.versionContent"
        class="d-flex dropdown-item align-items-center"
        @click="$emit('open-content-version')"
      >
        <div class="item-icon-wrapper">
          <font-awesome-icon
            class="mr-2 item-icon"
            :icon="['fad', 'history']"
          />
        </div>
        <span>{{ $t('knowledge.actions.display-versions') }}</span>
      </div>

      <!-- CREATE TEMPLATE OPTIONS -->
      <not-allowed-tooltip
        v-if="options.createTemplate"
        :user-permissions="userPermissions"
        permission-key="canCreateContent"
      >
        <template v-slot:main-content="{ hasPermissions }">
          <div
            class="d-flex dropdown-item align-items-center"
            @click="hasPermissions ? openCreateTemplate() : ''"
          >
            <div class="item-icon-wrapper">
              <font-awesome-icon
                class="mr-2 item-icon text-grey"
                :icon="['fad', 'file']"
              />
            </div>
            <span class="mb-0"
              >{{ $t('knowledge.actions.create-template') }}
            </span>
          </div>
        </template>
      </not-allowed-tooltip>

      <!-- ARCHIVE CONTENT OPTION -->
      <not-allowed-tooltip
        v-if="options.deleteContent"
        :user-permissions="userPermissions"
        permission-key="canDeleteContent"
      >
        <template v-slot:main-content="{ hasPermissions }">
          <div
            class="d-flex dropdown-item align-items-center"
            @click="hasPermissions ? openDeleteContent() : ''"
          >
            <div class="item-icon-wrapper">
              <font-awesome-icon
                class="mr-2 item-icon text-red"
                :icon="['fad', 'trash']"
              />
            </div>
            <span>{{ $t('knowledge.actions.archive') }}</span>
          </div>
        </template>
      </not-allowed-tooltip>
    </base-dropdown>
    <ModalEvents
      modal-name="DeleteContentModal"
      :uid="deleteModalUniqueKey"
      @choice="archiveContent"
    />
    <ModalEvents
      modal-name="CreateTemplateModal"
      :uid="createTemplateUniqueKey"
      @choice="handleCreateTemplate"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import NotAllowedTooltip from '@/components/Commons/NotAllowedTooltip';
import { getWebTranslatedEntityUrl } from 'utils/adminToWebRouteMapping';

import ModalEvents from '@/components/Modals/ModalEvents';
export default {
  name: 'case-header-options',
  components: {
    ModalEvents,
    NotAllowedTooltip,
  },
  props: {
    options: {
      type: Object,
      default: () => ({
        contentSettings: false,
        copyToClipboard: false,
        deleteCase: false,
        cloneCase: false,
        deleteContent: false,
        versionContent: false,
        integrationImport: false,
        settings: false,
        createTemplate: false,
      }),
    },
    userPermissions: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    deleteModalUniqueKey() {
      return `${this.$options.name}-${this.focusKnowledge.id}`;
    },
    createTemplateUniqueKey() {
      return `${this.$options.name}-template-${this.focusKnowledge.id}`;
    },
    ...mapGetters('adminModule', ['companyHostname']),
    ...mapGetters('knowledgeModule', [
      'focusKnowledge',
      'focusContent',
      'focusKnowledgeIsPublic',
      'editingLanguage',
      'focusKnowledgeIsMultilinguale',
    ]),
  },
  methods: {
    async copyUrl() {
      try {
        const { name, params, query, hash } = await getWebTranslatedEntityUrl({
          entityId:
            this.$route.params.stepId ||
            this.$route.params.contentId ||
            this.$route.params.caseId,
          entityType: this.$route.params.caseId
            ? 'Case'
            : this.focusContent.type,
          lang: this.editingLanguage,
          parentId: this.$route.params.stepId
            ? this.focusContent.parentId
            : null,
          knowledgeId: this.focusKnowledge.id,
        });
        const { href } = this.$router.resolve({
          name,
          params,
          query,
          hash,
        });
        await navigator.clipboard.writeText(
          window.location.origin + `${href}${hash}`,
        );
        this.$message({
          message: this.$t(
            'knowledge.actions.copy-to-clipboard-confirmation-message',
          ),
          type: 'info',
        });
      } catch (e) {
        this.$message({
          message: this.$t('knowledge.actions.copy-to-clipboard-error-message'),
          type: 'error',
        });
      }
    },
    async openDeleteContent() {
      const contentId = this.focusContent.id;
      const relatedCount = await this.countRelatedContentReferences({
        contentId,
        knowledgeId: this.focusKnowledge.id,
      });
      const backlinks = await this.getContentBacklinks(contentId);
      const payload = {
        component: 'DeleteContentModal',
        uid: this.deleteModalUniqueKey,
        props: { display: true, id: contentId, backlinks, relatedCount },
      };
      this.openModal(payload);
    },
    async openCreateTemplate() {
      const payload = {
        component: 'CreateTemplateModal',
        uid: this.createTemplateUniqueKey,
        props: { display: true, name: this.focusContent.label },
      };
      this.openModal(payload);
    },
    async handleCreateTemplate({ name, description }) {
      const template = {
        name,
        description,
        contentId: this.focusContent.id,
      };

      let loadingMessage = this.$message({
        duration: 0,
        dangerouslyUseHTMLString: true,
        iconClass: 'display: none',
        message: `<i class="fas fa-spinner fa-spin" style="margin-right: 1em;"></i> ${this.$t(
          'knowledge.templates.create.loading',
        )}`,
      });

      try {
        const res = await this.createTemplateByContentId(template);

        if (!res) throw new Error('TEMPLATE_FAILED');
        loadingMessage.close();
        this.$message({
          message: this.$t('knowledge.templates.create.succeed'),
          type: 'success',
        });
        this.cover = false;
      } catch (e) {
        loadingMessage.close();
        this.$message({
          message: this.$t('knowledge.templates.create.failed'),
          type: 'error',
        });
      }
    },
    archiveContent(choice) {
      if (choice) this.$emit('archive');
    },
    ...mapActions('modalsModule', ['openModal']),
    ...mapActions('knowledgeModule', [
      'countRelatedContentReferences',
      'createTemplateByContentId',
      'getContentBacklinks',
    ]),
  },
};
</script>

<style lang="scss" scoped>
.nav-link-icon {
  padding: 4px;
  border-radius: 4px;
  color: $grey-7-mayday;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  &:hover {
    background-color: $grey-2-mayday;
  }
  .collection-dropdown-trigger-icon {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }
}
.dropdown {
  border-radius: 2px;
  padding-left: 4px;
  padding-right: 4px;
  font-size: 12px;
  cursor: pointer;
}

.dropdown-item {
  color: $grey-8-mayday;
  display: flex;
  align-items: center;

  &:hover {
    color: $blue-mayday;
  }
}

.item-icon-wrapper {
  width: 12px !important;
  height: 12px !important;
  display: flex;
  align-items: center;
  justify-self: center;
  margin-right: 8px;
}

.item-icon {
  font-size: 12px !important;
}
</style>
